import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import './lacarte.css';
import {
  groupWinesByColor, searchWines, sortWinesFlat, formatPrice,
  SORT_CYCLE, type LaCarteModel, type LaCarteWine,
} from './laCarteModel';
import Masthead, { THEME_CYCLE, type ThemeChoice } from './Masthead';
import StickyBar from './StickyBar';
import ColorRail, { type RailEntry } from './ColorRail';
import WineSection from './WineSection';
import DetailSheet, { type SheetMode } from './DetailSheet';
import PicksBar from './PicksBar';
import { useShortlist } from '../../../utils/shortlist';

/**
 * LA CARTE — the public wine page (approved Direction A design).
 *
 * Renders a normalized LaCarteModel (see laCarteModel.ts) so BOTH resolver
 * kinds — menu and legacy wine-list — present identically. Pure CSS per the
 * mockup; no MUI on this page.
 *
 * Theme: opens LIGHT explicitly; Auto (prefers-color-scheme) and Dark are
 * toggle options; a manual choice is remembered for the session and degrades
 * silently where storage is blocked.
 */

const THEME_STORAGE_KEY = 'lacarte-theme';

const readStoredTheme = (): ThemeChoice => {
  try {
    const s = window.sessionStorage.getItem(THEME_STORAGE_KEY);
    if (s === 'light' || s === 'auto' || s === 'dark') return s;
  } catch { /* storage blocked — fall through to the default */ }
  return 'light';
};

interface Props {
  model: LaCarteModel;
}

const LaCartePage: React.FC<Props> = ({ model }) => {
  const [theme, setTheme] = useState<ThemeChoice>(readStoredTheme);
  const [query, setQuery] = useState('');
  const [sortIndex, setSortIndex] = useState(0);
  const [isolated, setIsolated] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetMode, setSheetMode] = useState<SheetMode>({ kind: 'empty' });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const picks = useShortlist(model.slug);
  const pickedIds = useMemo(() => new Set(picks.ids), [picks.ids]);
  const sectionEls = useRef(new Map<string, HTMLElement>());
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cycleTheme = useCallback(() => {
    setTheme(prev => {
      const next = THEME_CYCLE[(THEME_CYCLE.indexOf(prev) + 1) % THEME_CYCLE.length];
      try { window.sessionStorage.setItem(THEME_STORAGE_KEY, next); } catch { /* blocked — session-only */ }
      return next;
    });
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1800);
  }, []);

  // ── data pipeline: search → isolate → group/sort ──
  const searched = useMemo(() => searchWines(model.wines, query), [model.wines, query]);
  const visible = useMemo(
    () => (isolated ? searched.filter(w => (w.color ?? 'OTHER') === isolated) : searched),
    [searched, isolated],
  );
  const sortMode = SORT_CYCLE[sortIndex].mode;
  const colorGroups = useMemo(
    () => (sortMode === 'color' ? groupWinesByColor(visible) : null),
    [visible, sortMode],
  );
  const flatSorted = useMemo(
    () => (sortMode === 'color' ? null : sortWinesFlat(visible, sortMode)),
    [visible, sortMode],
  );
  const railEntries: RailEntry[] = useMemo(
    () => groupWinesByColor(searched).map(g => ({ color: g.color, label: g.label })),
    [searched],
  );
  const wineById = useMemo(() => {
    const m = new Map<string, LaCarteWine>();
    model.wines.forEach(w => m.set(w.id, w));
    return m;
  }, [model.wines]);

  // ── rail semantics: jump → isolate → clear ──
  const onRailTap = useCallback((color: string) => {
    if (isolated === color) { setIsolated(null); return; }
    const colorSort = SORT_CYCLE[sortIndex].mode === 'color';
    if (!colorSort || (activeSection === color && !isolated)) { setIsolated(color); return; }
    setIsolated(null);
    sectionEls.current.get(color)?.scrollIntoView({ block: 'start' });
  }, [isolated, sortIndex, activeSection]);

  // ── scroll tracking: collapsing title + active rail section ──
  useEffect(() => {
    const onScroll = () => setCollapsed(window.scrollY > 170);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !colorGroups) return undefined;
    const obs = new IntersectionObserver(entries => {
      const hit = entries.find(e => e.isIntersecting);
      if (hit && hit.target.id) setActiveSection(hit.target.id.replace('lc-sec-', ''));
    }, { rootMargin: '-130px 0px -70% 0px' });
    sectionEls.current.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [colorGroups]);

  // ── detail / picks surfaces ──
  const isPane = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1200px)').matches;

  const openWine = useCallback((id: string) => {
    const w = wineById.get(id);
    if (!w) return;
    setSelectedId(id);
    setSheetMode({ kind: 'wine', wine: w });
    if (!isPane()) setSheetOpen(true);
  }, [wineById]);

  const openPicks = useCallback(() => {
    setSelectedId(null);
    setSheetMode({ kind: 'picks', picks: picks.ids.map(id => wineById.get(id)).filter((w): w is LaCarteWine => !!w) });
    if (!isPane()) setSheetOpen(true);
  }, [picks.ids, wineById]);

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    setSelectedId(null);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isPane()) closeSheet(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeSheet]);

  // Keep the picks sheet content live while it's showing.
  useEffect(() => {
    setSheetMode(prev => (prev.kind === 'picks'
      ? { kind: 'picks', picks: picks.ids.map(id => wineById.get(id)).filter((w): w is LaCarteWine => !!w) }
      : prev));
  }, [picks.ids, wineById]);

  const togglePick = useCallback((id: string) => { picks.toggle(id); }, [picks]);

  // ── share: native sheet with the canonical URL; clipboard fallback ──
  const shareMenu = useCallback(() => {
    const url = window.location.href;
    const title = `${model.venueName ?? ''} — ${model.title}`.replace(/^ — /, '');
    if (navigator.share) {
      navigator.share({ title, url }).catch(() => { /* guest dismissed */ });
      return;
    }
    (navigator.clipboard?.writeText(url) ?? Promise.reject()).then(
      () => showToast('Menu link copied'),
      () => showToast(url),
    );
  }, [model.venueName, model.title, showToast]);

  const sharePicks = useCallback(() => {
    const lines = picks.ids
      .map(id => wineById.get(id))
      .filter((w): w is LaCarteWine => !!w)
      .map(w => `· ${[w.producer, w.name, w.vintage ?? 'NV'].filter(Boolean).join(' ')} — ${formatPrice(w.minPrice, w.currency)}`);
    const text = `Our picks — ${model.venueName ?? model.title}\n${lines.join('\n')}`;
    (navigator.clipboard?.writeText(text) ?? Promise.reject()).finally(() => showToast('Copied for the table'));
  }, [picks.ids, wineById, model.venueName, model.title, showToast]);

  const registerSection = useCallback((color: string) => (el: HTMLElement | null) => {
    if (el) sectionEls.current.set(color, el);
    else sectionEls.current.delete(color);
  }, []);

  const subtitle = model.description
    ?? 'Tap a color to jump · tap it again to isolate';

  return (
    <div className={`lc ${collapsed ? 'lc-collapsed' : ''}`.trim()} data-theme={theme}>
      <Masthead
        venueName={model.venueName}
        venueLogoUrl={model.venueLogoUrl}
        subtitle={model.title}
        theme={theme}
        onCycleTheme={cycleTheme}
        onShare={shareMenu}
      />

      <div className="lc-largetitle">
        <h2>{model.title}</h2>
        <p>{subtitle}</p>
      </div>

      <StickyBar
        title={model.title}
        query={query}
        onQueryChange={setQuery}
        sortIndex={sortIndex}
        onCycleSort={() => setSortIndex(i => (i + 1) % SORT_CYCLE.length)}
      >
        {railEntries.length > 1 && (
          <ColorRail
            entries={railEntries}
            active={activeSection}
            isolated={isolated}
            onTap={onRailTap}
          />
        )}
      </StickyBar>

      <div className="lc-frame">
        <main className="lc-main" aria-live="polite">
          {colorGroups && colorGroups.map(g => (
            <WineSection
              key={g.color}
              id={`lc-sec-${g.color}`}
              label={g.label}
              color={g.color}
              wines={g.wines}
              selectedId={selectedId}
              pickedIds={pickedIds}
              onOpen={openWine}
              sectionRef={registerSection(g.color)}
            />
          ))}
          {flatSorted && (
            <WineSection
              id="lc-sec-all"
              label="All wines"
              color={null}
              wines={flatSorted}
              selectedId={selectedId}
              pickedIds={pickedIds}
              onOpen={openWine}
            />
          )}
          {visible.length === 0 && model.wines.length > 0 && (
            <section className="lc-section">
              <p style={{ color: 'var(--muted)', paddingTop: 20 }}>
                Nothing matches — try clearing the search.
              </p>
            </section>
          )}

          {/* Non-wine curated sections (menus only) — below the wine experience */}
          {model.otherSections.map(s => (
            <section className="lc-section" key={s.id}>
              <div className="lc-section-head">
                <h3>{s.name}</h3>
                <span className="lc-count">{s.items.length}</span>
              </div>
              {s.description && (
                <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 8 }}>{s.description}</p>
              )}
              <div>
                {s.items.map(it => (
                  <div className="lc-other-item" key={it.id}>
                    <span>
                      <span className="lc-name lc-serif">{it.name}</span>
                      {(it.subtitle || it.note) && (
                        <span className="lc-sub" style={{ display: 'block' }}>{it.subtitle ?? it.note}</span>
                      )}
                    </span>
                    <span className="lc-price">
                      {formatPrice(it.price ?? (it.formats.find(f => f.price != null)?.price ?? null), it.currency)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </main>

        <DetailSheet
          mode={sheetMode}
          open={sheetOpen}
          pickedIds={pickedIds}
          onTogglePick={togglePick}
          onSharePicks={sharePicks}
          onClose={closeSheet}
        />
      </div>

      <PicksBar count={picks.count} onOpen={openPicks} />
      <div className={`lc-toast ${toast ? 'lc-show' : ''}`.trim()} role="status">{toast ?? ''}</div>
    </div>
  );
};

export default LaCartePage;
